import { ResponsiveLine } from '@nivo/line'
import { numberFormat } from '@labrav/common'
import { TotalAndPercentage } from '../TotalAndPercentage/TotalAndPercentage'
import {
  themeForRevenueLineGraph,
  useStyles,
} from './RevenueOverviewChart.styles'
import { useTheme } from '@mui/material/styles'
import { CustomLegends } from '../CustomLegends/CustomLegends'
import React from 'react'
import { OverviewEmptyState } from '../OverviewEmptyState/OverviewEmptyState'
import { useSelector } from '../../../store/hooks'
import { Overview } from '../../../modules/flyoutOverview/types'
import OverviewEmptyStateImage from '../../../common/images/overviewEmptyState.png'
import { getCloudImg } from '../../../common/utils/getCloudImg'

export const RevenueOverviewChart = ({
  dateRangeDescription,
}: {
  dateRangeDescription: string
}) => {
  const { classes } = useStyles()
  const theme = useTheme()
  const lineGraphTheme = themeForRevenueLineGraph(
    theme.palette.labraText.description
  )
  const { revenue } = useSelector<Overview>(
    state => state.flyoutOverview?.overview
  )
  const revenueData = revenue?.revenueData ?? []
  const cloudTotals = revenueData.reduce(
    (acc, item) => {
      if (item.id === 'AWS') {
        acc.aws = item.cloudTotal ?? 0
      } else if (item.id === 'Microsoft') {
        acc.microsoft = item.cloudTotal ?? 0
      } else if (item.id === 'GCP') {
        acc.gcp = item.cloudTotal ?? 0
      }
      return acc
    },
    { aws: 0, microsoft: 0, gcp: 0 }
  )
  const isRevenueDataEmpty =
    !revenue?.revenueData || revenue.revenueData.length === 0

  return (
    <>
      {isRevenueDataEmpty ? (
        <OverviewEmptyState
          imgUrl={OverviewEmptyStateImage}
          containerHeight={34.75}
          imageHeight={12}
          imageWidth={16.75}
          description="No revenue has been generated by your product(s)."
        />
      ) : (
        <div data-testid={'revenue-chart'} className={classes.root}>
          <TotalAndPercentage
            total={revenue?.total}
            currency="$"
            percentage={revenue?.percentage}
            type={revenue?.rise}
            label="in last 1 year"
          />
          <div
            className={classes.legendsWrapper}
            data-testid={'custom-legend-wrapper'}
          >
            <CustomLegends
              legends={[
                {
                  label: 'AWS:',
                  legendColor: theme.palette.awsGraph.main,
                  legendSize: 'medium',
                  value: `USD ${numberFormat(cloudTotals.aws)}`,
                },
                {
                  label: 'Microsoft:',
                  legendColor: theme.palette.microsoftGraph.main,
                  legendSize: 'medium',
                  value: `USD ${numberFormat(cloudTotals.microsoft)}`,
                },
                {
                  label: 'GCP:',
                  legendColor: theme.palette.gcpGraph.main,
                  legendSize: 'medium',
                  value: `USD ${numberFormat(cloudTotals.gcp)}`,
                },
              ]}
            />
          </div>
          <div
            className={classes.responsiveLineWrapper}
            data-testid={'responsive-line-chart'}
          >
            <ResponsiveLine
              data={revenueData}
              theme={lineGraphTheme}
              curve="catmullRom"
              margin={{ top: 30, right: 15, bottom: 65, left: 35 }}
              xScale={{ type: 'point' }}
              yScale={{
                type: 'linear',
                stacked: false,
              }}
              gridYValues={5}
              axisBottom={{
                tickSize: 0,
                tickPadding: 10,
                legend: `(${dateRangeDescription} | Revenue in thousand dollars)`,
                legendOffset: 55,
                legendPosition: 'middle',
                tickValues: 5,
              }}
              axisLeft={{
                tickSize: 0,
                tickPadding: 10,
                legendOffset: -40,
                tickValues: 5,
                legendPosition: 'middle',
                format: value => numberFormat(value),
              }}
              pointSize={5}
              pointBorderColor={{ from: 'serieColor' }}
              useMesh={true}
              colors={[
                theme.palette.awsGraph.main,
                theme.palette.microsoftGraph.main,
                theme.palette.gcpGraph.main,
              ]}
              tooltip={point => {
                const imgSrc = getCloudImg(point?.point.serieId as string)
                const valueInUSD = numberFormat(
                  Number(point.point.data.yFormatted) * 1000
                )
                return (
                  <div className={classes.tooltip}>
                    {imgSrc && (
                      <div className={classes.imgIcon}>
                        <img src={imgSrc} height={'16px'} />
                      </div>
                    )}
                    USD {valueInUSD}
                  </div>
                )
              }}
            />
          </div>
        </div>
      )}
    </>
  )
}

export { getCloudImg }
